import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Row, Col, Avatar, Typography, Button, Space, Statistic, Progress, Tag, message, Modal,
  Alert, List, Form, Input, ColorPicker, Divider, Tooltip, App, Spin
} from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import {
  User, Crown, FileText, MessageSquare, Settings, LogOut, Upload, Edit, Trash2, Plus,
  Lock, CrownIcon
} from 'lucide-react';
import authService from '../services/authService';
import avatarService from '../services/avatarService';
import vipService from '../services/vipService';
import noteService from '../services/noteService';
import { getAllTags } from '../services/api';
import cacheManager from '../services/cacheManager';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const { Title, Text } = Typography;

const UserDashboard: React.FC = () => {
  const { modal } = App.useApp();
  const [user, setUser] = useState(authService.getCurrentUser());
  const [vipStatus, setVipStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [tagCounts, setTagCounts] = useState<{ [key: number]: number }>({});
  const [stats, setStats] = useState<any>(null);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [vipPlans, setVipPlans] = useState<any[]>([]);
  const [aiUsage, setAiUsage] = useState<any>(null);
  const [noteUsage, setNoteUsage] = useState<any>(null);
  const [formatUsage, setFormatUsage] = useState<any>(null);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [newTagColor, setNewTagColor] = useState('#1890ff');
  const [updating, setUpdating] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<any>(null);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [vipModalVisible, setVipModalVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 使用ref来防止React StrictMode导致的重复执行
  const hasInitialized = useRef(false);

  useEffect(() => {
    // 防止React StrictMode导致的重复执行
    if (hasInitialized.current) {
      console.log('UserDashboard: 跳过重复初始化（React StrictMode）');
      return;
    }
    hasInitialized.current = true;

    console.log('UserDashboard: 开始初始化数据');
    loadDataFromCache();
  }, []);

  // 监听缓存更新事件
  useEffect(() => {
    const handleCacheUpdate = (type: string, data: any) => {
      console.log(`UserDashboard: 收到缓存更新事件 - ${type}`);

      switch (type) {
        case 'noteStats':
          setStats(data);
          break;
        case 'vipStatus':
          setVipStatus(data);
          break;
        case 'aiUsage':
          console.log('👤 UserDashboard: 收到AI使用情况更新:', data);
          setAiUsage(data);
          break;
        case 'noteUsage':
          setNoteUsage(data);
          break;
        case 'formatUsage':
          setFormatUsage(data);
          break;
        case 'tags':
          // 重新处理标签数据
          if (data && stats?.tagStats) {
            const tagCountMap: { [key: number]: number } = {};
            stats.tagStats.forEach((stat: any) => {
              tagCountMap[stat.tagId] = stat.count;
            });

            const tagsWithCounts = data.map((tag: any) => ({
              ...tag,
              count: tagCountMap[tag.id] || 0
            }));
            setTags(tagsWithCounts);
          } else {
            setTags(data || []);
          }
          break;
      }
    };

    console.log('👤 UserDashboard: 开始监听缓存更新事件');
    cacheManager.on('updated', handleCacheUpdate);

    return () => {
      console.log('👤 UserDashboard: 停止监听缓存更新事件');
      cacheManager.off('updated', handleCacheUpdate);
    };
  }, [stats]);

  // 从缓存加载数据
  const loadDataFromCache = async () => {
    try {
      setLoading(true);
      console.log('UserDashboard: 从缓存加载数据');

      // 确保缓存已初始化
      await cacheManager.initialize();

      // 获取缓存数据
      const cacheData = cacheManager.getData();

      // 设置状态
      if (cacheData.noteStats) setStats(cacheData.noteStats);
      if (cacheData.vipStatus) setVipStatus(cacheData.vipStatus);
      if (cacheData.aiUsage) setAiUsage(cacheData.aiUsage);
      if (cacheData.noteUsage) setNoteUsage(cacheData.noteUsage);
      if (cacheData.formatUsage) setFormatUsage(cacheData.formatUsage);

      // 处理标签数据
      if (cacheData.tags && cacheData.noteStats?.tagStats) {
        const tagCountMap: { [key: number]: number } = {};
        cacheData.noteStats.tagStats.forEach((stat: any) => {
          tagCountMap[stat.tagId] = stat.count;
        });

        const tagsWithCounts = cacheData.tags.map((tag: any) => ({
          ...tag,
          count: tagCountMap[tag.id] || 0
        }));
        setTags(tagsWithCounts);
      } else {
        setTags(cacheData.tags || []);
      }

      console.log('UserDashboard: 缓存数据加载完成');
      setError(null);
    } catch (err) {
      console.error('UserDashboard: 从缓存加载数据失败:', err);
      setError('获取数据失败');
    } finally {
      setLoading(false);
    }
  };



  // 获取格式化使用情况
  const getFormatUsage = async () => {
    try {
      const response = await api.get('/format/usage');
      return response.data;
    } catch (error) {
      return { todayUsed: 0, dailyLimit: 1, remaining: 1 };
    }
  };

  // 刷新标签数据（用于标签操作后的刷新）
  const refreshTags = async () => {
    try {
      // 使用缓存管理器更新标签数据
      await cacheManager.updateCache('tags');
      await cacheManager.updateCache('noteStats');

      // 从缓存获取最新数据
      const cacheData = cacheManager.getData();

      if (cacheData.tags && cacheData.noteStats?.tagStats) {
        const tagCountMap: { [key: number]: number } = {};
        cacheData.noteStats.tagStats.forEach((stat: any) => {
          tagCountMap[stat.tagId] = stat.count;
        });

        const tagsWithCounts = cacheData.tags.map((tag: any) => ({
          ...tag,
          count: tagCountMap[tag.id] || 0
        }));
        setTags(tagsWithCounts);
      } else {
        setTags(cacheData.tags || []);
      }
    } catch (error) {
      console.error('刷新标签失败:', error);
      message.error('刷新标签失败');
    }
  };

  const showEditTagModal = (tag: any) => {
    setEditingTag(tag);
    setNewTagColor(tag.color || '#1890ff');
    setTagModalVisible(true);
  };

  const showAddTagModal = () => {
    setEditingTag(null);
    setNewTagColor('#1890ff');
    setTagModalVisible(true);
  };

  const handleTagSubmit = async (values: any) => {
    try {
      setUpdating(true);

      if (editingTag) {
        // 编辑现有标签
        await api.put(`/tags/${editingTag.id}`, {
          name: values.tagName,
          color: newTagColor
        });
        message.success(`标签 "${editingTag.name}" 已更新为 "${values.tagName}"`);
      } else {
        // 添加新标签
        await api.post('/tags', {
          name: values.tagName,
          color: newTagColor
        });
        message.success(`标签 "${values.tagName}" 已添加`);
      }

      setTagModalVisible(false);
      await refreshTags();
      // 更新缓存
      cacheManager.updateCache('tags').catch(err => console.warn('更新标签缓存失败:', err));
      cacheManager.updateCache('noteStats').catch(err => console.warn('更新笔记统计缓存失败:', err));
    } catch (error) {
      message.error(editingTag ? '更新标签失败' : '添加标签失败');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTag = (tagId: number, tagName: string) => {
    const tag = tags.find(t => t.id === tagId);
    const noteCount = tag?.count || 0;
    setTagToDelete({ id: tagId, name: tagName, count: noteCount });
    setDeleteModalVisible(true);
  };

  const confirmDeleteTag = async () => {
    if (!tagToDelete) return;

    try {
      await api.delete(`/tags/${tagToDelete.id}`);
      message.success(`标签 "${tagToDelete.name}" 已删除`);
      setDeleteModalVisible(false);
      setTagToDelete(null);
      await refreshTags();
    } catch (error) {
      message.error('删除标签失败');
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      setAvatarUploading(true);

      // 预处理头像
      const processedFile = await avatarService.preprocessAvatar(file);

      // 上传头像
      const result = await avatarService.uploadAvatar(processedFile);

      if (result.success) {
        // 更新用户信息
        const updatedUser = { ...user!, avatar: result.avatarUrl };
        authService.updateUserInfo(updatedUser);
        setUser(updatedUser);
        message.success('头像上传成功');
      } else {
        message.error(result.message || '头像上传失败');
      }
    } catch (error) {
      message.error('头像上传失败');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // 验证文件
        const validation = avatarService.validateImageFile(file);
        if (!validation.valid) {
          message.error(validation.message);
          return;
        }
        handleAvatarUpload(file);
      }
    };
    input.click();
  };

  const handleUpgradeVip = () => {
    // 这里可以打开VIP升级模态框
    message.info('VIP升级功能开发中...');
  };

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      content: '确定要退出登录吗？',
      onOk: () => {
        authService.logout();
        message.success('已退出登录');
      }
    });
  };

  if (!user) {
    return (
      <div className="user-dashboard">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text>请先登录</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-dashboard">
        <LoadingSpinner
          text="加载用户数据中..."
          height="300px"
        />
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      {/* 用户信息卡片 */}
      <Card className="user-info-card" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} sm={6} md={4}>
            <div style={{ textAlign: 'center' }}>
              <div className="avatar-upload-area" onClick={handleAvatarClick}>
                <Avatar
                  size={80}
                  src={avatarService.getDisplayAvatarUrl(user.avatar, user.username)}
                  className="user-avatar"
                  style={{ cursor: 'pointer' }}
                >
                  {!user.avatar && user.username.charAt(0).toUpperCase()}
                </Avatar>
                <div className="avatar-upload-overlay">
                  <Upload className="upload-icon" size={20} />
                </div>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={18} md={14}>
            <div className="user-info">
              <Title level={3} style={{ marginBottom: 8 }}>{user.username}</Title>
              <Text type="secondary">{user.email}</Text>
              <div style={{ marginTop: 12 }}>
                <Tag color={vipStatus?.isVip ? "gold" : "default"} icon={vipStatus?.isVip ? <Crown size={12} /> : null}>
                  {vipStatus?.isVip ? "VIP会员" : "普通用户"}
                </Tag>
                {vipStatus?.isVip && vipStatus.expirationDate && (
                  <Tag color="blue">
                    到期时间: {new Date(vipStatus.expirationDate).toLocaleDateString()}
                    {vipStatus.daysRemaining !== null && ` (剩余${vipStatus.daysRemaining}天)`}
                  </Tag>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
            <Space direction="vertical">
              <Button type="primary" icon={<Edit size={16} />} onClick={() => setProfileModalVisible(true)}>
                修改信息
              </Button>
              <Button icon={<Lock size={16} />} onClick={() => setPasswordModalVisible(true)}>
                修改密码
              </Button>

            </Space>
          </Col>
        </Row>
      </Card>

      {/* 会员权益卡片 */}
      <Card title={<><Crown size={16} style={{ marginRight: 8 }} /> 会员权益</>} className="vip-card" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col xs={24} md={16}>
            <div className="vip-info-section">
              {!vipStatus?.isVip ? (
                <Alert
                  message="免费用户限制"
                  description={
                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                      <li>AI每天使用上限10次</li>
                      <li>最多保存1000个笔记</li>
                      <li>没有尊贵的VIP标识</li>
                      <li>无法享受优先客服</li>
                      <li>一键排版每天只能用一次</li>
                    </ul>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <Alert
                  message="VIP会员特权"
                  description={
                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                      <li>AI无限制使用</li>
                      <li>无限制保存笔记</li>
                      <li>尊贵的VIP标识</li>
                      <li>优先客服支持</li>
                      <li>一键排版无限制使用</li>
                    </ul>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="usage-section">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Statistic
                    title="笔记数量"
                    value={noteUsage?.totalCount || stats?.totalNotes || 0}
                    suffix={vipStatus?.isVip ?
                      <Tag color="gold">无限制</Tag> :
                      `/ ${noteUsage?.limit || 1000}`
                    }
                  />
                  {!vipStatus?.isVip && (
                    <Progress
                      percent={Math.round((noteUsage?.totalCount || 0) / (noteUsage?.limit || 1000) * 100)}
                      status={noteUsage?.remaining === 0 ? "exception" : "active"}
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </Col>
                <Col span={24}>
                  <Statistic
                    title="AI对话使用情况"
                    value={aiUsage?.todayCount || 0}
                    suffix={vipStatus?.isVip ?
                      <Tag color="gold">无限制</Tag> :
                      `/ ${aiUsage?.limit || 10}`
                    }
                  />
                  {!vipStatus?.isVip && (
                    <Progress
                      percent={Math.round((aiUsage?.todayCount || 0) / (aiUsage?.limit || 10) * 100)}
                      status={aiUsage?.remaining === 0 ? "exception" : "active"}
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </Col>
                <Col span={24}>
                  <Statistic
                    title="一键排版使用情况"
                    value={formatUsage?.todayUsed || 0}
                    suffix={vipStatus?.isVip ?
                      <Tag color="gold">无限制</Tag> :
                      `/ ${formatUsage?.dailyLimit || 1}`
                    }
                  />
                  {!vipStatus?.isVip && (
                    <Progress
                      percent={Math.round((formatUsage?.todayUsed || 0) / (formatUsage?.dailyLimit || 1) * 100)}
                      status={formatUsage?.remaining === 0 ? "exception" : "active"}
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Divider />
        <div className="vip-action" style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<Crown size={16} />}
            size="large"
            onClick={() => setVipModalVisible(true)}
          >
            {vipStatus?.isVip ? '续费会员' : '开通会员'}
          </Button>
        </div>
      </Card>

      {/* 标签管理卡片 */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>标签管理</span>
            <Button type="primary" icon={<Plus size={16} />} onClick={showAddTagModal}>
              添加标签
            </Button>
          </div>
        }
        className="tags-card"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <List
              className="tag-list"
              itemLayout="horizontal"
              dataSource={[...tags].sort((a, b) => (b.count || 0) - (a.count || 0))}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      type="text"
                      icon={<Edit size={16} />}
                      onClick={() => showEditTagModal(item)}
                    >
                      编辑
                    </Button>,
                    <Button
                      type="text"
                      danger
                      icon={<Trash2 size={16} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTag(item.id, item.name);
                      }}
                    >
                      删除
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: item.color
                        }}
                      />
                    }
                    title={
                      <Space>
                        <Tag color={item.color}>{item.name}</Tag>
                        <span>使用次数: {item.count || 0}</span>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ padding: '20px' }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Text strong>标签使用分布</Text>
              </div>
              {tags.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={tags.slice(0, 8).map(tag => ({
                        name: tag.name,
                        value: tag.count || 0,
                        color: tag.color || '#1890ff'
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {tags.slice(0, 8).map((tag, index) => (
                        <Cell key={`cell-${index}`} fill={tag.color || '#1890ff'} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => [
                        `${value} 篇笔记`,
                        name
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>
                  <Text type="secondary">
                    暂无标签数据
                  </Text>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>



      {/* 标签编辑模态框 */}
      <Modal
        title={editingTag ? "编辑标签" : "添加标签"}
        open={tagModalVisible}
        onCancel={() => setTagModalVisible(false)}
        footer={null}
      >
        <Form
          onFinish={handleTagSubmit}
          initialValues={{
            tagName: editingTag?.name || ''
          }}
        >
          <Form.Item
            label="标签名称"
            name="tagName"
            rules={[
              { required: true, message: '请输入标签名称' },
              { max: 20, message: '标签名称不能超过20个字符' }
            ]}
          >
            <Input placeholder="请输入标签名称" />
          </Form.Item>
          <Form.Item label="标签颜色">
            <ColorPicker
              value={newTagColor}
              onChange={(color) => setNewTagColor(color.toHexString())}
              presets={[
                {
                  label: '推荐',
                  colors: [
                    '#1890ff', '#52c41a', '#faad14', '#f5222d',
                    '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'
                  ]
                }
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setTagModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={updating}>
                {editingTag ? '更新' : '添加'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 删除标签确认模态框 */}
      <Modal
        title="删除标签"
        open={deleteModalVisible}
        onOk={confirmDeleteTag}
        onCancel={() => setDeleteModalVisible(false)}
        okText="删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>
          确定要删除标签 <strong>"{tagToDelete?.name}"</strong> 吗？
        </p>
        {tagToDelete?.count > 0 && (
          <Alert
            message={`该标签被 ${tagToDelete.count} 篇笔记使用，删除后这些笔记将失去该标签。`}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Modal>

      {/* VIP升级模态框 */}
      <Modal
        title="升级VIP会员"
        open={vipModalVisible}
        onCancel={() => setVipModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Crown size={48} style={{ color: '#faad14', marginBottom: 16 }} />
          <Title level={3}>开通VIP会员</Title>
          <Text type="secondary">解锁所有高级功能，享受无限制使用</Text>

          <div style={{ margin: '24px 0' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Card hoverable>
                  <Title level={4}>月度会员</Title>
                  <div style={{ fontSize: '24px', color: '#1890ff', marginBottom: 8 }}>
                    ¥9.9<span style={{ fontSize: '14px' }}>/月</span>
                  </div>
                  <Button type="primary" block>
                    立即开通
                  </Button>
                </Card>
              </Col>
              <Col span={12}>
                <Card hoverable style={{ border: '2px solid #faad14' }}>
                  <div style={{ position: 'absolute', top: -10, right: 10, background: '#faad14', color: 'white', padding: '2px 8px', borderRadius: 10, fontSize: 12 }}>
                    推荐
                  </div>
                  <Title level={4}>年度会员</Title>
                  <div style={{ fontSize: '24px', color: '#faad14', marginBottom: 8 }}>
                    ¥99<span style={{ fontSize: '14px' }}>/年</span>
                  </div>
                  <Text type="secondary" style={{ textDecoration: 'line-through' }}>
                    原价 ¥118.8
                  </Text>
                  <Button type="primary" block style={{ background: '#faad14', borderColor: '#faad14' }}>
                    立即开通
                  </Button>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Modal>
    </div >
  );
};

export default UserDashboard;
